import axios from "axios";
import { PaginatedResponse } from "../types/api";
import { API_BASE_URL_NO_API } from "../utils/apiConfig";

const API_BASE_URL = API_BASE_URL_NO_API;

export interface ClientApiResponse {
    id: number;
    name: string;
    email: string;
    phone: string;
    address?: string;
    website?: string;
    notes?: string;
    caretaker: number | null;
    caretaker_name?: string;
}

export type PaginatedClientsResponse = PaginatedResponse<ClientApiResponse>;

export interface GetClientsParams {
    search?: string;
    caretaker?: number;
    page?: number;
    page_size?: number;
}

export interface CreateClientRequest {
    name: string;
    email: string;
    phone: string;
    address: string;
    website?: string;
    notes?: string;
    caretaker_id?: number | null;
}

export interface UpdateClientRequest {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    website?: string;
    notes?: string;
    caretaker_id?: number | null;
}

const getBaseUrl = () => {
    return API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;
};

export const clientsApi = {
    /**
     * Pobiera klientów z backendu z paginacją
     * @param params - Opcjonalne parametry wyszukiwania i paginacji
     * @returns Paginowana odpowiedź z API zawierająca count, next, previous i results
     */
    getClients: async (params?: GetClientsParams): Promise<PaginatedClientsResponse> => {
        const queryParams = new URLSearchParams();
        if (params?.search) {
            queryParams.append("search", params.search);
        }
        if (params?.caretaker) {
            queryParams.append("caretaker", params.caretaker.toString());
        }
        if (params?.page) {
            queryParams.append("page", params.page.toString());
        }
        if (params?.page_size) {
            queryParams.append("page_size", params.page_size.toString());
        }

        const queryString = queryParams.toString();
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/clients/${queryString ? `?${queryString}` : ""}`;

        const response = await axios.get<PaginatedResponse<ClientApiResponse>>(url);
        return response.data;
    },

    /**
     * Pobiera szczegóły klienta po ID
     * @param id - ID klienta
     * @returns Szczegóły klienta
     */
    getClient: async (id: number): Promise<ClientApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/clients/${id}/`;
        const response = await axios.get<ClientApiResponse>(url);
        return response.data;
    },

    /**
     * Tworzy nowego klienta
     * @param data - Dane klienta do utworzenia
     * @returns Utworzony klient
     */
    createClient: async (data: CreateClientRequest): Promise<ClientApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/clients/`;
        const response = await axios.post<ClientApiResponse>(url, data);
        return response.data;
    },

    /**
     * Aktualizuje klienta
     * @param id - ID klienta
     * @param data - Dane do aktualizacji (wszystkie pola opcjonalne)
     * @returns Zaktualizowany klient
     */
    updateClient: async (id: number, data: UpdateClientRequest): Promise<ClientApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/clients/${id}/`;
        const response = await axios.patch<ClientApiResponse>(url, data);
        return response.data;
    },

    /**
     * Usuwa klienta
     * @param id - ID klienta do usunięcia
     */
    deleteClient: async (id: number): Promise<void> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/clients/${id}/`;
        await axios.delete(url);
    },
};

